import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Trophy, TrendingUp, LogOut, User as UserIcon } from 'lucide-react';
import { auth } from '../serices/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();

  // 1. Listen for Auth State changes globally
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Categories', path: '/categories' },
    { name: 'Pulse', path: '/pulse' }, // Added the Leaderboard link
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    if (window.confirm("Sign out of your account?")) {
      signOut(auth);
    }
  };

  return (
    <nav className="fixed top-0 w-full z-[100] bg-black/80 backdrop-blur-lg border-b border-white/10 font-sans">
      <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
        
        {/* LOGO */}
        <Link to="/" className="flex items-center gap-2 group">
          <Trophy className="text-[#D4AF37] group-hover:rotate-12 transition-transform" size={24} />
          <span className="text-xl font-black italic tracking-tighter uppercase">
            MTTA <span className="text-[#D4AF37]">2026</span>
          </span>
        </Link>

        {/* DESKTOP NAV */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <Link 
              key={link.name} 
              to={link.path}
              className={`text-[10px] font-black tracking-[0.3em] uppercase transition-colors hover:text-[#D4AF37] ${
                isActive(link.path) ? 'text-[#D4AF37]' : 'text-white/60'
              }`}
            >
              <div className="flex items-center gap-2">
                {link.name === 'Pulse' && <TrendingUp size={12} />}
                {link.name}
              </div>
            </Link>
          ))}
          
          {/* AUTH LOGIC: Show User Profile if logged in, else show Nominate button */}
          {user ? (
  <div className="flex items-center gap-5 border-l border-white/10 pl-10">
    <div className="text-right hidden sm:block">
      <p className="text-[8px] font-black text-[#D4AF37] uppercase tracking-widest leading-none mb-1">
        Authenticated
      </p>
      <p className="text-[10px] text-white font-bold uppercase tracking-tighter">
        {user.displayName?.split(' ')[0]}
      </p>
    </div>
    
    <div className="relative group">
      {/* 1. If photoURL exists, show it with no-referrer policy */}
      {user.photoURL ? (
        <img 
          src={user.photoURL} 
          referrerPolicy="no-referrer" 
          alt="profile" 
          className="w-10 h-10 rounded-full border border-[#D4AF37]/40 p-0.5 object-cover"
          onError={(e) => {
            // 2. Backup: If image fails to load, replace with a UI Avatar or hide
            e.target.onerror = null; 
            e.target.src = `https://ui-avatars.com/api/?name=${user.displayName}&background=D4AF37&color=000`;
          }}
        />
      ) : (
        // 3. Fallback: Show a generic user icon if no photo exists
        <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center bg-zinc-900">
          <UserIcon size={18} className="text-[#D4AF37]" />
        </div>
      )}

      <button 
        onClick={handleLogout}
        className="absolute -top-1 -right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
      >
        <LogOut size={10} />
      </button>
    </div>
  </div>
) : (
  <Link to="/nominate">
    <button className="bg-[#D4AF37] text-black px-6 py-2.5 text-[10px] font-black tracking-widest uppercase hover:bg-white transition-all">
      Nominate Now
    </button>
  </Link>
)}
        </div>

        {/* MOBILE TOGGLE */}
        <div className="flex items-center gap-4 md:hidden">
          {user && (
             <img src={user.photoURL}
             referrerPolicy="no-referrer"
             className="w-8 h-8 rounded-full border border-[#D4AF37]/40" 
             alt="me" />
          )}
          <button className="text-white p-2" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X size={28} /> : <Menu size={28} className="text-[#D4AF37]" />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {isOpen && (
        <div className="md:hidden bg-black border-b border-white/10 p-8 flex flex-col gap-8 animate-in slide-in-from-top duration-500 shadow-2xl">
          {navLinks.map((link) => (
            <Link 
              key={link.name} 
              to={link.path} 
              onClick={() => setIsOpen(false)}
              className={`text-xs font-black tracking-[0.3em] uppercase ${
                isActive(link.path) ? 'text-[#D4AF37]' : 'text-white/60'
              }`}
            >
              {link.name}
            </Link>
          ))}

          <div className="h-px bg-white/5 w-full" />

          {user ? (
            <button 
                onClick={() => { handleLogout(); setIsOpen(false); }}
                className="flex items-center gap-4 text-red-500 text-xs font-black uppercase tracking-[0.3em]"
            >
                <LogOut size={18} /> Sign Out Session
            </button>
          ) : (
            <Link to="/nominate" onClick={() => setIsOpen(false)}>
              <button className="w-full bg-[#D4AF37] text-black py-5 text-[11px] font-black tracking-[0.3em] uppercase rounded-xl">
                Start Nomination
              </button>
            </Link>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;










/*import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Trophy } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Categories', path: '/categories' },
    /*{ name: 'Vote', path: '/vote' },*/
   /* { name: 'Admin', path: '/admin' },*//*
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 w-full z-[100] bg-black/80 backdrop-blur-lg border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
        
        {/* LOGO *//*
        <Link to="/" className="flex items-center gap-2 group">
          <Trophy className="text-[#D4AF37] group-hover:rotate-12 transition-transform" size={24} />
          <span className="text-xl font-black italic tracking-tighter uppercase">
            MTTA <span className="text-[#D4AF37]">2026</span>
          </span>
        </Link>

        {/* DESKTOP LINKS *//*
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <Link 
              key={link.name} 
              to={link.path}
              className={`text-[10px] font-bold tracking-[0.3em] uppercase transition-colors hover:text-[#D4AF37] ${
                isActive(link.path) ? 'text-[#D4AF37]' : 'text-white/60'
              }`}
            >
              {link.name}
            </Link>
          ))}
          
          <Link to="/nominate">
            <button className="bg-[#D4AF37] text-black px-6 py-2.5 text-[10px] font-black tracking-widest uppercase hover:bg-white transition-all duration-300">
              Nominate Now
            </button>
          </Link>
        </div>

        {/* MOBILE TOGGLE *//*
        <button className="md:hidden text-white" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* MOBILE MENU *//*
      {isOpen && (
        <div className="md:hidden bg-black border-b border-white/10 p-6 flex flex-col gap-6 animate-in slide-in-from-top duration-300">
          {navLinks.map((link) => (
            <Link 
              key={link.name} 
              to={link.path} 
              onClick={() => setIsOpen(false)}
              className="text-xs font-bold tracking-[0.2em] uppercase text-white/60 hover:text-[#D4AF37]"
            >
              {link.name}
            </Link>
          ))}
          <Link to="/nominate" onClick={() => setIsOpen(false)}>
            <button className="w-full bg-[#D4AF37] text-black py-4 text-[10px] font-black tracking-widest uppercase">
              Nominate Now
            </button>
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navbar;*/